import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Award, Users, Aperture, Camera, Film, Eye } from 'lucide-react';
import WhatWeDo from '@/components/WhatWeDo';
import Testimonials from '@/components/Testimonials';

const HERO_IMAGE = '/images/About/About_Hero.jpeg';

const STATS = [
  { icon: Aperture, value: '50+', label: 'Projects Delivered' },
  { icon: Users, value: '35+', label: 'Happy Clients' },
  { icon: Award, value: '2+', label: 'Years of Excellence' },
];

const PILLARS = [
  { icon: Camera, title: 'Artistic Vision', desc: 'Every frame is composed with an eye for beauty, storytelling, and emotional resonance.' },
  { icon: Film, title: 'Cinematic Craft', desc: 'We bring filmmaking techniques to photography — creating images that move and inspire.' },
  { icon: Eye, title: 'Attention to Detail', desc: 'From lighting to post-production, every detail is meticulously refined to perfection.' },
];

export default function AboutPage() {
  return (
    <>
      {/* Hero */}
      <section className="relative h-[50vh] sm:h-[60vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="About Visyra" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-t from-visyra-bg via-visyra-bg/60 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-12 w-full">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-body text-xs uppercase tracking-[0.3em] text-gold mb-3">About Us</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            data-testid="about-page-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-visyra-text">
            Our Story
          </motion.h1>
        </div>
      </section>

      {/* Brand Story */}
      <section data-testid="about-section" className="py-24 sm:py-32 bg-visyra-bg">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-visyra-text mb-6 leading-tight">
              Where Art Meets <span className="italic text-gold">Emotion</span>
            </h2>
            <div className="section-divider mb-8" />
            <div className="space-y-6 font-body text-sm sm:text-base text-visyra-muted leading-relaxed">
              <p>
                At Visyra, we believe every frame should tell a story. Our work is driven by a deep passion for cinematic visual storytelling—where emotion, composition, and intention come together to create images that feel timeless rather than ordinary.
              </p>
              <p>
                Visyra was created with a clear vision: to move beyond simple documentation and approach photography and cinematography as a form of visual art. Every project is treated as an opportunity to translate real moments into carefully crafted visuals that resonate long after the moment has passed.
              </p>
              <p>
                We are visual storytellers at heart. Behind every image is a narrative waiting to be expressed—whether it’s the quiet intimacy of a bridal portrait, the energy of a fashion shoot, or the clarity and impact required in a commercial campaign. Our approach adapts to the story, not the other way around.
              </p>
              <p>
                Our team combines creative instinct with technical precision, blending modern tools with a refined eye for detail. From weddings and editorials to events and brand campaigns, we bring the same level of focus, care, and creative discipline to every project—ensuring a seamless experience from shoot to delivery.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24 sm:py-32 bg-visyra-paper">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="text-center mb-16">
            <p className="font-body text-xs uppercase tracking-[0.3em] text-gold mb-4">Our Philosophy</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-visyra-text">What Drives Us</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {PILLARS.map((pillar, i) => {
              const Icon = pillar.icon;
              return (
                <motion.div key={pillar.title} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.15 }}
                  className="text-center p-8 bg-visyra-card/50 border border-white/5 hover:border-gold/20 transition-all duration-500">
                  <Icon size={28} className="text-gold mx-auto mb-5" strokeWidth={1.5} />
                  <h3 className="font-heading text-xl text-visyra-text mb-3">{pillar.title}</h3>
                  <p className="font-body text-sm text-visyra-muted leading-relaxed">{pillar.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 sm:py-32 bg-visyra-bg">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-3 gap-8">
            {STATS.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <motion.div key={stat.label} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}
                  data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`} className="text-center">
                  <Icon size={24} className="text-gold mx-auto mb-3" strokeWidth={1.5} />
                  <p className="font-heading text-3xl sm:text-4xl text-visyra-text tracking-tight">{stat.value}</p>
                  <p className="font-body text-xs text-visyra-dim uppercase tracking-wider mt-2">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <WhatWeDo />

      {/* Testimonials */}
      <Testimonials />

      {/* CTA */}
      <section className="py-24 sm:py-32 bg-visyra-paper">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-visyra-text mb-6">
              Let's Create Something <span className="italic text-gold">Beautiful</span>
            </h2>
            <p className="font-body text-sm sm:text-base text-visyra-muted leading-relaxed mb-10 max-w-lg mx-auto">
              We'd love to learn about your vision and discuss how we can bring it to life.
            </p>
            <Link to="/contact" data-testid="about-cta-btn"
              className="btn-shimmer inline-block font-body text-sm font-medium uppercase tracking-widest bg-gold text-visyra-bg px-10 py-4 hover:bg-gold-light transition-all duration-400">
              Get in Touch
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
